import { regionalPrices, historicalPeriod } from '../shared/regional_prices.js';
import { Controller } from '@hotwired/stimulus';

// National curves remain server-rendered. Explicit region selection fetches
// cached state aggregates; stale responses never overwrite the current scope.
export default class extends Controller {
  static values = { url: String };
  static targets = ['countLabel', 'chip', 'series', 'hit', 'tooltip', 'matrixCol', 'matrixHead', 'matrixSelect', 'matrixCell', 'select', 'regionSelect', 'summaryData', 'summary', 'scope', 'count', 'median', 'date', 'status', 'nationalPanel', 'regionalPanel'];

  connect() {
    this.nationalSummaries = this.hasSummaryDataTarget ? JSON.parse(this.summaryDataTarget.textContent) : null;
    this.nationalOptions = this.nationalSummaries?.options || (this.hasSelectTarget ? [...this.selectTarget.options].map(o => ({ value: o.value, label: o.textContent })) : []);
    this.regionalCache = new Map();
    this.regionCode = '';
    this.selectedSlug = 'all';
    this.requestId = 0;
    this.onTrimSelected = (event) => this.activate(event.detail.slug);
    this.onScopeChanged = event => {
      this.scopeAcknowledged = true;
      this.selectedSlug = event.detail.slug;
      if (this.regionSelectTarget.value !== event.detail.state) {
        this.regionSelectTarget.value = event.detail.state;
        this.changeRegion();
      } else {
        this.activate(event.detail.slug);
        if (this.regionalData) this.publishRegional(this.regionCode, this.regionalData);
      }
    };
    window.addEventListener('seo:scope-changed', this.onScopeChanged);
    window.addEventListener('seo:trim-selected', this.onTrimSelected);
    if (this.hasRegionSelectTarget) {
      this.regionSelectTarget.value = '';
      this.changeRegion();
    }
    window.dispatchEvent(new CustomEvent('seo:request-scope'));
  }

  disconnect() {
    this.requestId++;
    this.abortRequest?.abort();
    window.removeEventListener('seo:trim-selected', this.onTrimSelected);
    window.removeEventListener('seo:scope-changed', this.onScopeChanged);
  }

  select(event) {
    this.activate(event.currentTarget.dataset.trim);
  }

  changeSelection(event) {
    this.requestScope(event.currentTarget.value);
    this.activate(event.currentTarget.value);
    if (this.hasMatrixSelectTarget && event.currentTarget === this.matrixSelectTarget) {
      const head = this.matrixHeadTargets.find(node => node.dataset.trim === event.currentTarget.value);
      const area = head?.closest('.price-mileage-matrix__scrollarea');
      if (area) {
        const stickyWidth = head.parentElement.firstElementChild.getBoundingClientRect().width;
        const left = area.scrollLeft + head.getBoundingClientRect().left - area.getBoundingClientRect().left - stickyWidth;
        area.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }
  }

  requestScope(slug = this.selectedSlug) {
    this.scopeAcknowledged = false;
    window.dispatchEvent(new CustomEvent('seo:change-scope', { detail: { state: this.regionSelectTarget.value, slug } }));
    // Sparse cohorts can expose these filters without a modelled-estimate
    // configurator. Still publish one scope to the table and history.
    if (!this.scopeAcknowledged) {
      window.dispatchEvent(new CustomEvent('seo:scope-changed', { detail: {
        state: this.regionSelectTarget.value, slug,
        stateLabel: this.regionSelectTarget.selectedOptions?.[0]?.textContent || 'United States',
        trimLabel: this.nationalOptions?.find(option => option.value === slug)?.label || 'All trims',
      } }));
    }
  }

  publishRegional(state, data) {
    window.dispatchEvent(new CustomEvent('seo:regional-data', { detail: { state, data } }));
  }

  activate(slug) {
    if (this.regionCode) {
      if (!this.regionalData) return;
      this.selectedSlug = slug;
      if (!this.regionalData.series.some(s => s.slug === slug)) {
        this.regionalPanelTarget.hidden = true;
        this.statusTarget.textContent = 'Not enough qualifying listings for this trim in the selected state. Choose another trim or All trims.';
        this.updateSummary(this.regionalData.state_name, this.nationalOptions.find(o => o.value === slug)?.label || slug, null, null);
        return;
      }
      this.statusTarget.textContent = '';
      this.selectTarget.value = this.selectedSlug;
      this.renderRegional();
      return;
    }
    if (!this.seriesTargets.some((g) => g.dataset.trim === slug) && !this.nationalOptions?.some(option => option.value === slug)) return;
    this.selectedSlug = slug;
    if (this.hasSelectTarget) this.selectTarget.value = slug;
    if (this.hasMatrixSelectTarget) this.matrixSelectTarget.value = slug;
    this.matrixCellTargets.forEach(cell => cell.classList.toggle('price-mileage-matrix__cell--selected', cell.dataset.trim === slug));
    if (this.nationalSummaries) {
      const data = this.nationalSummaries.summaries[slug];
      this.updateSummary('United States', data?.label || 'All trims', data?.count, data?.median);
      this.dateTarget.textContent = `National snapshot · ${this.nationalSummaries.date}`;
    }
    if (this.hasTooltipTarget) this.hideTip();
    this.hitTargets.forEach((hit) => {
      if (hit.dataset.trim) {
        const selected = hit.dataset.trim === slug;
        hit.dataset.selected = String(selected);
        hit.setAttribute('tabindex', selected ? '0' : '-1');
        hit.setAttribute('aria-hidden', String(!selected));
      }
    });
    this.seriesTargets.forEach((g) => g.classList.toggle('seo-mchart__series--active', g.dataset.trim === slug));
    this.chipTargets.forEach((c) => c.classList.toggle('seo-mchart__chip--active', c.dataset.trim === slug));
    this.matrixColTargets.forEach((c) => c.classList.toggle('seo-mchart__col--active', c.dataset.trim === slug));
    this.matrixHeadTargets.forEach((h) => h.classList.toggle('seo-mchart__colhead--active', h.dataset.trim === slug));
  }

  updateSummary(region, trim, count, median) {
    if (this.hasCountLabelTarget) this.countLabelTarget.textContent = this.regionalData?.basis === 'historical' ? 'Vehicles listed · last 3 months' : 'Active listings';
    const number = value => Number.isFinite(Number(value)) && value != null ? Number(value).toLocaleString('en-US') : '—';
    this.scopeTarget.textContent = `${region} · ${trim}`;
    this.countTarget.textContent = number(count);
    this.medianTarget.textContent = median != null ? `$${number(median)}` : '—';
  }

  setOptions(options) {
    this.selectTarget.replaceChildren(...options.map(({ value, label }) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      return option;
    }));
  }

  async changeRegion(event) {
    if (event) { this.requestScope(); if (this.regionCode !== this.regionSelectTarget.value) return this.changeRegion(); return; }
    const id = ++this.requestId;
    this.abortRequest?.abort();
    this.regionCode = this.regionSelectTarget.value;
    this.regionalData = null;
    this.statusTarget.textContent = '';
    this.regionalPanelTarget.hidden = true;
    this.nationalPanelTarget.hidden = Boolean(this.regionCode);
    this.selectTarget.disabled = false;
    if (!this.regionCode) {
      this.setOptions(this.nationalOptions);
      this.activate(this.nationalOptions.some(o => o.value === this.selectedSlug) ? this.selectedSlug : 'all');
      return;
    }
    const code = this.regionCode;
    this.statusTarget.textContent = 'Loading actual state listings…';
    this.selectTarget.disabled = true;
    this.updateSummary(this.regionSelectTarget.selectedOptions[0].textContent, 'Loading…', null, null);
    this.dateTarget.textContent = '';
    try {
      let data = this.regionalCache.get(code);
      if (!data) {
        data = await regionalPrices(this.urlValue.replace('STATE', encodeURIComponent(code)));
        this.regionalCache.set(code, data);
      }
      if (id !== this.requestId) return;
      this.regionalData = data;
      this.publishRegional?.(code, data);
      this.setOptions(this.nationalOptions);
      this.selectTarget.disabled = !(data.series?.length);
      this.statusTarget.textContent = data.status === 'empty' ? 'No qualifying active listings or listings from the last 3 months in this state. Choose another state or United States.' : '';
      if (!data.series?.length) {
        this.updateSummary(data.state_name, 'All trims', data.count, null);
        return;
      }
      this.activate(this.selectedSlug);
    } catch (error) {
      if (id !== this.requestId || error.name === 'AbortError') return;
      this.publishRegional?.(code, { status: 'unavailable' });
      this.statusTarget.textContent = 'State data is temporarily unavailable. Choose another state or return to United States to see the national snapshot.';
      this.updateSummary(this.regionSelectTarget.selectedOptions[0].textContent, 'Unavailable', null, null);
    }
  }

  renderRegional() {
    const series = this.regionalData.series.find(s => s.slug === this.selectedSlug);
    if (!series) return;
    this.updateSummary(this.regionalData.state_name, series.slug === 'all' ? 'All trims' : series.label, series.count, series.median);
    const observed = new Date(`${this.regionalData.observed_at}T12:00:00`);
    this.dateTarget.textContent = this.regionalData.basis === 'historical' ? historicalPeriod(this.regionalData) : `Current inventory · checked ${Number.isNaN(observed.getTime()) ? this.regionalData.observed_at : observed.toLocaleDateString('en-US')} · may differ from the national snapshot`;
    const panel = this.regionalPanelTarget;
    panel.replaceChildren();
    panel.hidden = false;
    const points = series.points || [];
    if (points.length < 2) {
      const message = document.createElement('p');
      message.textContent = 'Not enough mileage bands to draw a reliable curve for this selection. The summary above covers its qualifying listings.';
      panel.append(message);
      return;
    }
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 1000 420');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', `${this.regionalData.state_name}, ${series.label}: median asking price by odometer mileage`);
    const el = (tag, attributes, text) => {
      const node = document.createElementNS(ns, tag);
      Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
      if (text != null) node.textContent = text;
      svg.append(node);
      return node;
    };
    const low = Math.min(...points.map(p => p.p25)) * 0.9;
    const high = Math.max(...points.map(p => p.p75)) * 1.08;
    const maxMiles = Math.max(...points.map(p => p.miles)) * 1.06;
    const x = miles => 100 + miles / maxMiles * 850;
    const y = price => 350 - (price - low) / Math.max(1, high - low) * 315;
    for (let i = 0; i <= 4; i++) {
      const price = low + (high - low) * i / 4;
      el('line', { x1: 100, x2: 950, y1: y(price), y2: y(price), stroke: '#dfebf2' });
      el('text', { x: 85, y: y(price) + 7, 'text-anchor': 'end' }, `$${Math.round(price / 1000)}K`);
    }
    // Separate runs: never connect across a suppressed, thin mileage band.
    const runs = [];
    points.forEach(point => {
      const run = runs[runs.length - 1];
      if (!run || point.bucket > run[run.length - 1].bucket + 1) runs.push([point]);
      else run.push(point);
    });
    runs.forEach(run => {
      if (run.length > 1) {
        const band = [...run.map(p => `${x(p.miles)},${y(p.p75)}`), ...[...run].reverse().map(p => `${x(p.miles)},${y(p.p25)}`)].join(' ');
        el('polygon', { points: band, fill: '#d6e9f5' });
        el('polyline', { points: run.map(p => `${x(p.miles)},${y(p.median)}`).join(' '), fill: 'none', stroke: '#3789BB', 'stroke-width': 4 });
      }
    });
    points.forEach(p => {
      const dot = el('circle', { cx: x(p.miles), cy: y(p.median), r: 7, fill: '#3789BB', stroke: '#fff', 'stroke-width': 2 });
      const title = document.createElementNS(ns, 'title');
      title.textContent = `${p.label}: $${p.median.toLocaleString('en-US')}, ${p.count} listings`;
      dot.append(title);
    });
    for (let miles = 0; miles <= maxMiles; miles += maxMiles > 90000 ? 30000 : 15000) {
      el('text', { x: x(miles), y: 390, 'text-anchor': 'middle' }, `${miles / 1000}K mi`);
    }
    const plot = document.createElement('div');
    plot.className = 'price-chart-regional__plot';
    plot.append(svg);
    panel.append(plot);
    const table = document.createElement('table');
    table.className = 'seo-table';
    const caption = table.createCaption();
    caption.textContent = `${this.regionalData.state_name} · ${series.label} · actual mileage bands`;
    const header = table.createTHead().insertRow();
    ['Mileage (odometer)', 'Listings', 'Median asking price', 'Middle 50%'].forEach(label => {
      const th = document.createElement('th'); th.textContent = label; header.append(th);
    });
    const tbody = table.createTBody();
    points.forEach(p => {
      const row = tbody.insertRow();
      [p.label, p.count.toLocaleString('en-US'), `$${p.median.toLocaleString('en-US')}`, `$${p.p25.toLocaleString('en-US')}–$${p.p75.toLocaleString('en-US')}`].forEach(value => { row.insertCell().textContent = value; });
    });
    const details = document.createElement('details');
    details.className = 'seo-light-details';
    const summary = document.createElement('summary'); summary.textContent = 'View the selected state’s mileage data';
    const tableWrap = document.createElement('div');
    tableWrap.className = 'seo-tablewrap';
    tableWrap.append(table);
    details.append(summary, tableWrap);
    panel.append(details);
  }

  // Trim-лейбли походять зі скрапнутих даних — будуємо textContent-ом, не innerHTML.
  showTip(event) {
    const d = event.currentTarget.dataset;
    const tip = this.tooltipTarget;
    tip.textContent = '';
    const title = document.createElement('strong');
    title.textContent = `${d.series} · ${d.label}`;
    const line = document.createElement('div');
    line.textContent = `${d.median} median · ${d.range} · ${d.count} listings`;
    tip.append(title, line);
    tip.hidden = false;
    const plot = tip.parentElement.getBoundingClientRect();
    const dot = event.currentTarget.getBoundingClientRect();
    tip.style.left = `${dot.left - plot.left + dot.width / 2}px`;
    tip.style.top = `${dot.top - plot.top - 10}px`;
  }

  hideTip() {
    this.tooltipTarget.hidden = true;
  }
}
